import { FormattedMessage, useIntl } from 'react-intl'
import { LabelWithIndicator, Typography } from '@carto/meridian-ds/components'
import { HelpOutline } from '@mui/icons-material'
import { Box, Divider, FormControlLabel, Paper, Switch, Tooltip, styled } from '@mui/material'

import { useAppDispatch, useAppSelector } from 'app/state'
import { FeatureFlag } from 'app/types'
import { useFeatureFlag } from 'app/ui/useFeatureFlag'
import useUserPermissions from 'features/account/state/useUserPermissions'
import { getAgent, setAgentEnabledForViewer, updateMapSettings } from 'features/maps/state/mapsSlice'
import { MapSettings } from 'features/maps/types'
import { useBasemaps } from 'features/builder/hooks/builderMapHooks'
import { mapCommentsEnabledSelector } from 'features/builder/model/mapComments'
import { useGovernanceMaps } from 'features/common/hooks/useGovernanceMaps'
import useMapSharingInfo from 'features/maps/state/useMapSharingInfo'

const RootPaper = styled(Paper)(({ theme }) => ({
  width: '320px',
  borderRadius: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  maxHeight: `calc(100vh - ${theme.spacing(7.5)})`,
  paddingBottom: theme.spacing(1)
}))

const SectionTitle = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.5, 0, 0.5, 1.5),
  ...theme.typography.caption,
  color: theme.palette.text.secondary
}))

const MapSettingFormControl = styled(FormControlLabel)(({ theme }) => ({
  justifyContent: 'space-between',
  width: '100%',
  padding: theme.spacing(0.75, 2)
}))

const Header = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2, 2, 2),
  borderBottom: `1px solid ${theme.palette.divider}`
}))

const ScrollBox = styled(Box)(({ theme }) => ({
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  width: '100%',
  paddingTop: theme.spacing(1)
}))

function MapSettingSwitch({ propName, disabled }: { propName: keyof MapSettings; disabled?: boolean }) {
  const intl = useIntl()
  const dispatch = useAppDispatch()

  const value = useAppSelector((state) => state.maps.mapSettings[propName])
  const tooltip = intl.formatMessage({ id: `builder.mapSettings.propsTooltips.${propName}` })

  const handleChange = () => {
    dispatch(updateMapSettings({ [propName]: !value }))
  }

  return (
    <Box>
      <MapSettingFormControl
        disabled={disabled}
        aria-label={propName}
        labelPlacement="start"
        control={<Switch name={propName} checked={Boolean(value)} onChange={handleChange} color="primary" />}
        label={
          <LabelWithIndicator
            label={<FormattedMessage id={`builder.mapSettings.props.${propName}`} />}
            icon={
              <Tooltip title={tooltip}>
                <HelpOutline />
              </Tooltip>
            }
          />
        }
      />
    </Box>
  )
}

function SharedMapSettingsPane() {
  const dispatch = useAppDispatch()
  const { exportViewportDataAvailable, exportPdfReportAvailable } = useUserPermissions()
  const commentsEnabled = useAppSelector(mapCommentsEnabledSelector)
  const hasSqlParameters = useAppSelector((state) => state.builder.sqlParameters.length > 0)
  const agent = useAppSelector(getAgent)
  const { mapStyles } = useBasemaps()
  const basemapSelectorEnabled = useFeatureFlag(FeatureFlag.BasemapSelectorInLegend)
  const isAgentFeatureFlagEnabled = useFeatureFlag(FeatureFlag.GenAIV1)
  const isReorderLayersFlagEnabled = useFeatureFlag(FeatureFlag.BuilderReorderLayersInLegend)
  const { allowUsersToManagePDFReports, allowUsersToManageViewportDataExport } = useGovernanceMaps()
  const currentMap = useAppSelector((state) => state.maps.currentMap)
  const { isPublicMap } = useMapSharingInfo(currentMap)

  // As per now, we want to prevent users to enable the Agent in Public Maps with querySources enabled
  const isPublicMapWithAgentQuerySources = isPublicMap && agent?.config && agent.config.capabilities?.querySources
  const isAgentToggleDisabled = isPublicMapWithAgentQuerySources || !agent?.config
  const shouldDisplayAgentToggle = isAgentFeatureFlagEnabled && agent

  return (
    <RootPaper>
      <Header py={1} px={2}>
        <Typography variant="body2" weight="strong">
          <FormattedMessage id="builder.mapSettings.title" />
        </Typography>
      </Header>

      <ScrollBox>
        {shouldDisplayAgentToggle && (
          <>
            <Box>
              <SectionTitle>
                <FormattedMessage id="builder.mapSettings.ai" />
              </SectionTitle>

              <MapSettingFormControl
                disabled={isAgentToggleDisabled}
                labelPlacement="start"
                control={
                  <Switch
                    name="agent"
                    disabled={isAgentToggleDisabled}
                    checked={agent.enabledForViewer}
                    onChange={(event) => {
                      const checked = event.target.checked

                      dispatch(setAgentEnabledForViewer(checked))
                      if (checked) {
                        const mapSettings: Partial<MapSettings> = { featureSelectionTool: true }
                        if (isReorderLayersFlagEnabled) mapSettings.reorderLayers = true
                        dispatch(updateMapSettings(mapSettings))
                      }
                    }}
                    color="primary"
                  />
                }
                label={
                  <LabelWithIndicator
                    label={<FormattedMessage id={'builder.mapSettings.props.agent'} />}
                    icon={
                      <Tooltip title={<FormattedMessage id={'builder.mapSettings.propsTooltips.agent'} />}>
                        <HelpOutline />
                      </Tooltip>
                    }
                  />
                }
              />
            </Box>

            <Divider />
          </>
        )}
        <Box>
          <SectionTitle>
            <FormattedMessage id="builder.mapSettings.interactivity" />
          </SectionTitle>
          <MapSettingSwitch propName="featureSelectionTool" />
          {isReorderLayersFlagEnabled && (
            <MapSettingSwitch propName="reorderLayers" disabled={agent?.enabledForViewer} />
          )}
          <MapSettingSwitch propName="sqlParameterControls" disabled={!hasSqlParameters} />
          {allowUsersToManageViewportDataExport && (
            <MapSettingSwitch propName="exportViewportData" disabled={!exportViewportDataAvailable} />
          )}
          {allowUsersToManagePDFReports && (
            <MapSettingSwitch propName="exportPDF" disabled={!exportPdfReportAvailable} />
          )}
          {commentsEnabled && <MapSettingSwitch propName="comments" />}
        </Box>
        <Divider />
        <Box>
          <SectionTitle>
            <FormattedMessage id="builder.mapSettings.navigation" />
          </SectionTitle>
          <MapSettingSwitch propName="addressSearchBar" />
          <MapSettingSwitch propName="showMeasureDistanceTool" />
          <MapSettingSwitch propName="scrollWheelZoom" />
          <MapSettingSwitch propName="showMyLocationButton" />
        </Box>
        <Divider />
        {basemapSelectorEnabled && (
          <>
            <Box>
              <SectionTitle>
                <FormattedMessage id="builder.mapSettings.basemaps" />
              </SectionTitle>
              <MapSettingSwitch propName="basemapsSelector" disabled={mapStyles.length <= 1} />
            </Box>
            <Divider />
          </>
        )}
        <Box>
          <SectionTitle>
            <FormattedMessage id="builder.mapSettings.display" />
          </SectionTitle>
          <MapSettingSwitch propName="showTitlePublicMap" />
        </Box>
        <Divider />
        <Box>
          <SectionTitle>
            <FormattedMessage id="builder.mapSettings.notifications" />
          </SectionTitle>
          <MapSettingSwitch propName="showPerformanceWarnings" />
        </Box>
      </ScrollBox>
    </RootPaper>
  )
}

export default SharedMapSettingsPane
